// pages/employees.js
import React, { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import {
  FaExclamationTriangle,
  FaSearch,
  FaFilter,
  FaUsers,
} from "react-icons/fa";

/* Theme */
const COLORS = {
  text: "#0f172a",
  subtext: "#475569",
  border: "#e2e8f0",
  card: "#ffffff",
  primary: "#f20c53",
  secondary: "#fb6c33",
  page: "#f5f6f8",
  ok: "#16a34a",
  warn: "#b45309",
  danger: "#b91c1c",
};

export default function Employees() {
  const [employees, setEmployees] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // filters
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");

  // permission
  const hasPermission =
    user?.role === "super_admin" ||
    (user?.permissions || []).includes("view_employees");

  useEffect(() => {
    const u = JSON.parse(localStorage.getItem("employee"));
    setUser(u || null);
  }, []);

  useEffect(() => {
    if (!hasPermission) return;
    const unsub = onSnapshot(collection(db, "employees"), (snap) => {
      const list = snap.docs.map((d) => ({
        docId: d.id,
        ...d.data(),
      }));
      setEmployees(list);
      setLoading(false);
    });
    return () => unsub && unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPermission]);

  // generate role choices from data
  const allRoles = useMemo(() => {
    const s = new Set();
    employees.forEach((e) => e.role && s.add(String(e.role)));
    return ["All", ...Array.from(s).sort()];
  }, [employees]);

  const filtered = useMemo(() => {
    let arr = [...employees];

    // search
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      arr = arr.filter((e) => {
        const hay = [
          e.name,
          e.mobile,
          e.pin,
          e.role,
          e.status,
          (e.permissions || []).join(" "),
        ]
          .join(" ")
          .toLowerCase();
        return hay.includes(s);
      });
    }

    // filters
    if (roleFilter !== "All") {
      arr = arr.filter((e) => String(e.role || "").toLowerCase() === roleFilter.toLowerCase());
    }
    if (statusFilter !== "All") {
      arr = arr.filter(
        (e) => String(e.status || "").toLowerCase() === statusFilter.toLowerCase()
      );
    }

    // sort: active → pending → others, then by name
    const rank = { active: 0, pending: 1 };
    arr.sort((a, b) => {
      const ar = rank[String(a.status || "").toLowerCase()] ?? 99;
      const br = rank[String(b.status || "").toLowerCase()] ?? 99;
      if (ar !== br) return ar - br;
      return (a.name || "").localeCompare(b.name || "");
    });

    return arr;
  }, [employees, q, roleFilter, statusFilter]);

  if (user && !hasPermission) {
    return (
      <div style={blurOverlay}>
        <div style={popupBox}>
          <FaExclamationTriangle size={24} color="#e53935" />
          <h3 style={{ marginTop: "0.5rem" }}>Access Denied</h3>
          <p>You do not have permission to view this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Hero */}
      <div style={styles.hero}>
        <div style={styles.heroLeft}>
          <div style={styles.heroIcon}>
            <FaUsers />
          </div>
          <div>
            <h2 style={styles.heroTitle}>Employees</h2>
            <p style={styles.heroSub}>Live list with search & filters</p>
          </div>
        </div>
        <div style={styles.counters}>
          <Badge tone="ok">Active: {employees.filter((e) => e.status === "active").length}</Badge>
          <Badge tone="warn">
            Pending: {employees.filter((e) => e.status === "pending").length}
          </Badge>
          <Badge tone="danger">
            Others: {employees.filter((e) => !["active", "pending"].includes(String(e.status))).length}
          </Badge>
        </div>
      </div>

      {/* Controls */}
      <div style={styles.controlsRow}>
        <div style={styles.searchBox}>
          <FaSearch style={{ opacity: 0.7 }} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, mobile, role, status, permission…"
            style={styles.searchInput}
          />
        </div>
        <div style={styles.controlGroup}>
          <FaFilter />
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            style={styles.selectBare}
          >
            {allRoles.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>
        <div style={styles.controlGroup}>
          <FaFilter />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={styles.selectBare}
          >
            <option>All</option>
            <option value="active">active</option>
            <option value="pending">pending</option>
            <option value="inactive">inactive</option>
            <option value="blocked">blocked</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div style={styles.tableWrap}>
        {loading ? (
          <div style={{ padding: 14, color: COLORS.subtext }}>Loading…</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <Th sticky>Name</Th>
                  <Th sticky>Mobile</Th>
                  <Th sticky>PIN</Th>
                  <Th sticky>Role</Th>
                  <Th sticky>Status</Th>
                  <Th sticky>Permissions</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ padding: 12, color: COLORS.subtext }}>
                      No employees found.
                    </td>
                  </tr>
                ) : (
                  filtered.map((emp, i) => {
                    const bg = i % 2 ? "#fafafa" : "#fff";
                    return (
                      <tr
                        key={emp.docId}
                        style={{
                          borderTop: `1px solid ${COLORS.border}`,
                          background: bg,
                          transition: "background .15s",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = bg)}
                      >
                        <Td>{emp.name || "—"}</Td>
                        <Td mono title={emp.mobile || ""}>{emp.mobile || "—"}</Td>
                        <Td mono title={emp.pin || ""}>{emp.pin || "—"}</Td>
                        <Td>{emp.role || "—"}</Td>
                        <Td>
                          <StatusChip status={emp.status} />
                        </Td>
                        <Td>{Array.isArray(emp.permissions) ? emp.permissions.length : 0}</Td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* Atoms */
function Th({ children, sticky }) {
  return (
    <th
      style={{
        textAlign: "left",
        padding: 10,
        fontWeight: 800,
        color: COLORS.text,
        background: "#f8fafc",
        position: sticky ? "sticky" : "static",
        top: 0,
        zIndex: 5,
        borderBottom: `1px solid ${COLORS.border}`,
      }}
    >
      {children}
    </th>
  );
}
function Td({ children, mono, title }) {
  return (
    <td
      title={title}
      style={{
        padding: 10,
        color: COLORS.text,
        verticalAlign: "top",
        fontFamily: mono ? "ui-monospace, SFMono-Regular, Menlo, monospace" : "inherit",
      }}
    >
      {children}
    </td>
  );
}
function Badge({ tone = "ok", children }) {
  const map = {
    ok: { bg: "#ecfdf5", fg: COLORS.ok },
    warn: { bg: "#fff7ed", fg: COLORS.warn },
    danger: { bg: "#fef2f2", fg: COLORS.danger },
  };
  const v = map[tone] || map.ok;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 10px",
        borderRadius: 999,
        background: v.bg,
        color: v.fg,
        fontWeight: 800,
        border: `1px solid ${v.fg}33`,
      }}
    >
      {children}
    </span>
  );
}
function StatusChip({ status }) {
  const s = String(status || "").toLowerCase();
  const map = {
    active: { bg: "#ecfdf5", fg: COLORS.ok, label: "active" },
    pending: { bg: "#fff7ed", fg: COLORS.warn, label: "pending" },
    inactive: { bg: "#f1f5f9", fg: COLORS.subtext, label: "inactive" },
    blocked: { bg: "#fef2f2", fg: COLORS.danger, label: "blocked" },
  };
  const v = map[s] || { bg: "#f1f5f9", fg: COLORS.subtext, label: s || "—" };
  return (
    <span
      style={{
        padding: "4px 10px",
        borderRadius: 999,
        background: v.bg,
        color: v.fg,
        fontWeight: 800,
        border: `1px solid ${v.fg}33`,
        textTransform: "capitalize",
      }}
    >
      {v.label}
    </span>
  );
}

/* Styles */
const styles = {
  container: {
    padding: 16,
    fontFamily: "Inter, system-ui, sans-serif",
    backgroundColor: COLORS.page,
    minHeight: "100vh",
    display: "grid",
    gap: 12,
  },
  hero: {
    background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
    color: "#fff",
    padding: "14px 16px",
    borderRadius: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    boxShadow: "0 14px 26px rgba(0,0,0,0.12)",
  },
  heroLeft: { display: "flex", alignItems: "center", gap: 12 },
  heroIcon: {
    width: 42,
    height: 42,
    borderRadius: 10,
    display: "grid",
    placeItems: "center",
    background: "rgba(255,255,255,0.12)",
    border: "1px solid rgba(255,255,255,0.35)",
  },
  heroTitle: { margin: 0, fontSize: 20, fontWeight: 900, lineHeight: 1.1 },
  heroSub: { margin: "4px 0 0", opacity: 0.92, fontSize: 13 },
  counters: { display: "flex", gap: 8, flexWrap: "wrap" },

  controlsRow: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    alignItems: "center",
  },
  searchBox: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 10,
    padding: "10px 12px",
    background: "#fff",
    minWidth: 260,
    flex: 1,
  },
  searchInput: {
    border: "none",
    outline: "none",
    flex: 1,
    fontSize: 14,
  },
  controlGroup: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 10,
    padding: "8px 10px",
    background: "#fff",
  },
  selectBare: {
    border: "none",
    outline: "none",
    background: "transparent",
    fontSize: 14,
    paddingRight: 4,
    cursor: "pointer",
  },

  tableWrap: {
    background: "#fff",
    borderRadius: 12,
    overflow: "hidden",
    boxShadow: "0 6px 14px rgba(0,0,0,.06)",
    border: `1px solid ${COLORS.border}`,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: 820,
    fontSize: 14,
  },
};

/* Access denied overlay styles (kept from your version) */
const blurOverlay = {
  backdropFilter: "blur(6px)",
  backgroundColor: "rgba(0, 0, 0, 0.4)",
  width: "100vw",
  height: "100vh",
  position: "fixed",
  top: 0,
  left: 0,
  zIndex: 9999,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};
const popupBox = {
  background: "#fff",
  padding: "2rem",
  borderRadius: "10px",
  width: "90%",
  maxWidth: "420px",
  textAlign: "center",
  boxShadow: "0 8px 20px rgba(0,0,0,0.2)",
};
